import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import DeleteModal from "../../../components/DeleteModal";
import VehiclesHeader from "./_components/VehiclesHeader";
import VehiclesTable from "./_components/VehiclesTable";
import {
  useDeleteVehicleMutation,
  useGetVehicles,
} from "../../../../api/fleet-management/vehicles/vehicles.hooks";
import { filterVehicles, normalizeVehicles } from "./utils/vehicle.utils";

const Vehicles = () => {
  const history = useHistory();
  const { data, isLoading, isError, error } = useGetVehicles();
  const { mutate: deleteVehicle, isLoading: isDeleting } = useDeleteVehicleMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const vehicles = normalizeVehicles(data);

  const filteredVehicles = useMemo(() => {
    return filterVehicles(vehicles, searchTerm);
  }, [vehicles, searchTerm]);

  const handleEdit = (vehicleId) => {
    history.push(`/fleet-management/vehicles/update/${vehicleId}`);
  };

  const openDeleteModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setSelectedVehicle(null);
  };

  const handleConfirmDelete = () => {
    if (!selectedVehicle?._id) return;

    setFeedback({ type: "", message: "" });
    deleteVehicle(selectedVehicle._id, {
      onSuccess: () => {
        setFeedback({ type: "success", message: "Vehicle deleted successfully." });
        closeDeleteModal();
      },
      onError: (deleteError) => {
        setFeedback({
          type: "danger",
          message: deleteError?.response?.data?.message || "Failed to delete vehicle.",
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load vehicles. {error?.response?.data?.message || "Please try again."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <VehiclesHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          total={filteredVehicles.length}
          onAddVehicle={() => history.push("/fleet-management/vehicles/add")}
        />

        <div className="card-body">
          {feedback.message && (
            <Alert variant={feedback.type} className="mb-3">
              {feedback.message}
            </Alert>
          )}

          {filteredVehicles.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No vehicles found</p>
            </div>
          ) : (
            <VehiclesTable
              vehicles={filteredVehicles}
              onEdit={handleEdit}
              onDelete={openDeleteModal}
              isDeleting={isDeleting}
            />
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Vehicle"
        message={`Are you sure you want to delete ${selectedVehicle?.registrationNumber || "this vehicle"}? This action cannot be undone.`}
        confirmText="Delete Vehicle"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Vehicles;
